import type { CalendarDate } from "../shared/calendar-date";
import { requireFinitePositive, requirePercentage } from "../shared/validation";

export type GoalMode = "gain" | "lose" | "maintain";

interface GoalPhaseBase {
  readonly id: string;
  readonly mode: GoalMode;
  readonly effectiveFrom: CalendarDate;
  readonly createdAt: string;
}

export interface GainOrLossGoalPhase extends GoalPhaseBase {
  readonly mode: "gain" | "lose";
  /** Percentage of body weight per week. */
  readonly weeklyRatePercent: number;
}

export interface MaintenanceGoalPhase extends GoalPhaseBase {
  readonly mode: "maintain";
  readonly targetWeightKilograms: number;
  /** Symmetric percentage around targetWeightKilograms. */
  readonly tolerancePercent: number;
}

export type GoalPhase = GainOrLossGoalPhase | MaintenanceGoalPhase;

export function validateGoalPhase(goal: GoalPhase): GoalPhase {
  if (!goal.id.trim()) throw new Error("Goal id cannot be empty");
  if (goal.mode === "maintain") {
    requireFinitePositive(goal.targetWeightKilograms, "Maintenance target weight");
    requirePercentage(goal.tolerancePercent, "Maintenance tolerance", true);
  } else {
    requirePercentage(goal.weeklyRatePercent, "Weekly weight-change rate");
  }
  return goal;
}

export function maintenanceRange(goal: MaintenanceGoalPhase): { minimumKilograms: number; maximumKilograms: number } {
  const fraction = goal.tolerancePercent / 100;
  return {
    minimumKilograms: goal.targetWeightKilograms * (1 - fraction),
    maximumKilograms: goal.targetWeightKilograms * (1 + fraction),
  };
}

export function signedWeeklyRate(goal: GainOrLossGoalPhase): number {
  return goal.mode === "gain" ? goal.weeklyRatePercent : -goal.weeklyRatePercent;
}
